import { ChangeDetectorRef, Component, inject, Inject, Injector, OnDestroy, OnInit } from "@angular/core";
import { DOCUMENT, Location } from "@angular/common";
import { Platform } from "@angular/cdk/platform";
import { TranslateService } from "@ngx-translate/core";
import { combineLatest, Subject } from "rxjs";
import { filter, take, takeUntil } from "rxjs/operators";
import { FuseLegacyConfigService } from "@nx-workspace/fuse";
import { FuseSidebarService } from "@fuse/components/sidebar/sidebar.service";
import { SignalRAspNetCoreHelper } from "@shared/helpers/SignalRAspNetCoreHelper";
import { NavigationEnd, Router } from "@angular/router";
import { AppAuthService } from "@shared/auth/app-auth.service";
import { DigitalCertificateService } from "@vhealth/professional/digital-certificate";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import { coerceBooleanProperty } from "@angular/cdk/coercion";
import { MatInput } from "@angular/material/input";
import { MatSelect } from "@angular/material/select";
import { MatDialog } from "@angular/material/dialog";
import {
    PENDING_TERMS_OF_SERVICE_DIALOG_PANEL_CLASS,
    PendingTermsOfServiceSignatureComponent,
} from "@app/wapps/legal/pending-terms-of-service/pending-terms-of-service-signature.component";
import { PendingTermsOfServiceSignatureService } from "@app/wapps/legal/pending-terms-of-service/pending-terms-of-service-signature.service";
import { MigrationConsentService } from "@app/wapps/data-migration/migration-consent.service";
import { GoogleTagManagerService } from "angular-google-tag-manager";
import { HtmlInjectorService } from "@app/HtmlInjectorService";
import { PrescriptionConfigService } from "app/wapps/prescription/prescription-config.service";
import { NotifyService } from "@ui/notify";
import { NavigationBuilderService } from "@app/navigation/navigation-builder.service";
import { AppSessionService } from "@vadmin/account/auth";
import { FuseNavigationService } from "@fuse/components/navigation/navigation.service";
import { TranslocoService } from "@jsverse/transloco";
import { RecordingStateService, RecordingOverlayService, RecordingLimitService } from "@vhealth/transcription/state";
import { SncrBrokerCallbackService } from "@app/wapps/system/integration/sncr/sncr-broker-callback.service";
import { isSncrIntegrationEnabled } from "@app/wapps/system/integration/sncr/sncr.model";
import { SNCR_INTEGRATION_ROUTE } from "@app/wapps/system/integration/sncr/sncr-integration.routes";

@Component({
    selector: "app",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit, OnDestroy {
    _notificationService = inject(NotifyService);

    _fuseNavigationService = inject(FuseNavigationService);

    _navigationTools = inject(NavigationBuilderService);

    _cdr = inject(ChangeDetectorRef);

    private readonly _migrationConsentService = inject(MigrationConsentService);

    private readonly _transloco = inject(TranslocoService);

    private readonly _recordingState = inject(RecordingStateService);
    private readonly _location = inject(Location);
    // Injecting RecordingOverlayService ensures its constructor effect starts at app boot
    private readonly _recordingOverlay = inject(RecordingOverlayService);
    private readonly _recordingLimit = inject(RecordingLimitService);

    private readonly _sncrBrokerCallback = inject(SncrBrokerCallbackService);

    private readonly _injector = inject(Injector);

    fuseConfig: any;

    // Private
    private _unsubscribeAll: Subject<any>;

    constructor(
        @Inject(DOCUMENT) private document: any,
        private _fuseConfigService: FuseLegacyConfigService,
        private _fuseSidebarService: FuseSidebarService,
        private _translateService: TranslateService,
        private _platform: Platform,
        private _router: Router,
        private appSession: AppSessionService,
        private _appAuth: AppAuthService,
        iconRegistry: MatIconRegistry,
        sanitizer: DomSanitizer,
        private _gtmService: GoogleTagManagerService,
        public dialog: MatDialog,
        private _pendingTermsOfServiceSignatureService: PendingTermsOfServiceSignatureService,
        private _htmlInjectorService: HtmlInjectorService,
        private _prescriptionConfigService: PrescriptionConfigService,
        private _digitalCertificate: DigitalCertificateService
    ) {
        // console.log('HMR is not enabled for webpack-dev-server!');
        // Get default navigation

        iconRegistry.addSvgIcon("bell", sanitizer.bypassSecurityTrustResourceUrl("assets/images/icons/bell.svg"));

        iconRegistry.addSvgIcon(
            "calendar",
            sanitizer.bypassSecurityTrustResourceUrl("assets/images/icons/calendar.svg")
        );

        iconRegistry.addSvgIcon("chat", sanitizer.bypassSecurityTrustResourceUrl("assets/images/icons/chat.svg"));

        iconRegistry.addSvgIcon(
            "checklist",
            sanitizer.bypassSecurityTrustResourceUrl("assets/images/icons/checklist.svg")
        );

        iconRegistry.addSvgIcon("contact", sanitizer.bypassSecurityTrustResourceUrl("assets/images/icons/contact.svg"));

        iconRegistry.addSvgIcon("delete", sanitizer.bypassSecurityTrustResourceUrl("assets/images/icons/delete.svg"));

        iconRegistry.addSvgIcon("doctor", sanitizer.bypassSecurityTrustResourceUrl("assets/images/icons/doctor.svg"));

        iconRegistry.addSvgIcon("edit", sanitizer.bypassSecurityTrustResourceUrl("assets/images/icons/edit.svg"));

        iconRegistry.addSvgIcon("eye", sanitizer.bypassSecurityTrustResourceUrl("assets/images/icons/eye.svg"));

        iconRegistry.addSvgIcon("home", sanitizer.bypassSecurityTrustResourceUrl("assets/images/icons/home-run.svg"));

        iconRegistry.addSvgIcon("lanyard", sanitizer.bypassSecurityTrustResourceUrl("assets/images/icons/lanyard.svg"));

        iconRegistry.addSvgIcon("layout", sanitizer.bypassSecurityTrustResourceUrl("assets/images/icons/layout.svg"));

        iconRegistry.addSvgIcon("search", sanitizer.bypassSecurityTrustResourceUrl("assets/images/icons/search.svg"));

        iconRegistry.addSvgIcon(
            "settings",
            sanitizer.bypassSecurityTrustResourceUrl("assets/images/icons/settings.svg")
        );

        iconRegistry.addSvgIcon("user", sanitizer.bypassSecurityTrustResourceUrl("assets/images/icons/user.svg"));

        iconRegistry.addSvgIcon("mkt", sanitizer.bypassSecurityTrustResourceUrl("assets/images/icons/mkt.svg"));

        iconRegistry.addSvgIcon(
            "integrations",
            sanitizer.bypassSecurityTrustResourceUrl("assets/images/icons/integrations.svg")
        );

        iconRegistry.addSvgIcon(
            "patients",
            sanitizer.bypassSecurityTrustResourceUrl("assets/images/icons/patients.svg")
        );

        iconRegistry.addSvgIcon("report", sanitizer.bypassSecurityTrustResourceUrl("assets/images/icons/report.svg"));

        iconRegistry.addSvgIcon("mobile", sanitizer.bypassSecurityTrustResourceUrl("assets/images/icons/mobile.svg"));

        iconRegistry.addSvgIcon("vpay", sanitizer.bypassSecurityTrustResourceUrl("assets/images/icons/vpay.svg"));

        iconRegistry.addSvgIcon(
            "dashboard",
            sanitizer.bypassSecurityTrustResourceUrl("assets/images/icons/dashboard.svg")
        );

        iconRegistry.addSvgIcon(
            "financial",
            sanitizer.bypassSecurityTrustResourceUrl("assets/images/icons/financial.svg")
        );

        iconRegistry.addSvgIcon(
            "glasses",
            sanitizer.bypassSecurityTrustResourceUrl("../../../assets/images/icons/glasses-solid.svg")
        );

        iconRegistry.addSvgIcon(
            "mobile-sparkles",
            sanitizer.bypassSecurityTrustResourceUrl("../../../assets/images/icons/mobile-app.svg")
        );

        iconRegistry.addSvgIcon(
            "view-appointment",
            sanitizer.bypassSecurityTrustResourceUrl("../../../assets/images/icons/view-appointment.svg")
        );

        iconRegistry.addSvgIcon(
            "add-appointment",
            sanitizer.bypassSecurityTrustResourceUrl("../../../assets/images/icons/add-appointment.svg")
        );

        iconRegistry.addSvgIcon(
            "patient-group",
            sanitizer.bypassSecurityTrustResourceUrl("../../../assets/images/icons/patient-group.svg")
        );

        iconRegistry.addSvgIcon(
            "doctor",
            sanitizer.bypassSecurityTrustResourceUrl("../../../assets/images/icons/doctor.svg")
        );

        iconRegistry.addSvgIcon(
            "procedure",
            sanitizer.bypassSecurityTrustResourceUrl("../../../assets/images/icons/procedure.svg")
        );

        iconRegistry.addSvgIcon(
            "health-insurance",
            sanitizer.bypassSecurityTrustResourceUrl("../../../assets/images/icons/health-insurance.svg")
        );

        iconRegistry.addSvgIcon(
            "care-unit",
            sanitizer.bypassSecurityTrustResourceUrl("../../../assets/images/icons/care-unit.svg")
        );

        iconRegistry.addSvgIcon(
            "file",
            sanitizer.bypassSecurityTrustResourceUrl("../../../assets/images/icons/file.svg")
        );

        iconRegistry.addSvgIcon(
            "notification",
            sanitizer.bypassSecurityTrustResourceUrl("../../../assets/images/icons/notification.svg")
        );

        iconRegistry.addSvgIcon(
            "pencil",
            sanitizer.bypassSecurityTrustResourceUrl("../../../assets/images/icons/pencil.svg")
        );

        iconRegistry.addSvgIcon(
            "content",
            sanitizer.bypassSecurityTrustResourceUrl("../../../assets/images/icons/content.svg")
        );

        iconRegistry.addSvgIcon(
            "whatsapp",
            sanitizer.bypassSecurityTrustResourceUrl("../../../assets/images/icons/whatsapp.svg")
        );

        iconRegistry.addSvgIcon(
            "play-store",
            sanitizer.bypassSecurityTrustResourceUrl("../../../assets/icons/play-store.svg")
        );

        iconRegistry.addSvgIcon("ai", sanitizer.bypassSecurityTrustResourceUrl("../../../assets/icons/ai.svg"));

        iconRegistry.addSvgIcon("apple", sanitizer.bypassSecurityTrustResourceUrl("../../../assets/icons/apple.svg"));

        combineLatest([
            this._appAuth.onLogin.pipe(filter(Boolean)),
            this.appSession.onTenantInfoChanged.pipe(filter(Boolean)),
        ])
            .pipe(take(1))
            .subscribe(([value, tenantInfo]) => {
                if (value === true && !!this.appSession.userId) {
                    this._digitalCertificate.refresh().subscribe();

                    const navigation = this._navigationTools.buildNavigation(tenantInfo);

                    this._fuseNavigationService.register("main", navigation);
                    this._fuseNavigationService.setCurrentNavigation("main");

                    if (!this.appSession.isAdmin() || this.appSession.isImpersonating()) {
                        return;
                    }

                    this._pendingTermsOfServiceSignatureService
                        .checkForPendingTermsOfServiceSignature()
                        .subscribe((info) => {
                            if (info?.contentLink) {
                                const dialogRef = this.dialog.open(PendingTermsOfServiceSignatureComponent, {
                                    width: "90%",
                                    panelClass: PENDING_TERMS_OF_SERVICE_DIALOG_PANEL_CLASS,
                                    data: info,
                                    disableClose: info.required,
                                    closeOnNavigation: !info.required,
                                });
                                dialogRef.afterClosed().subscribe(() => this._migrationConsentService.checkAndOpen());
                            } else {
                                this._migrationConsentService.checkAndOpen();
                            }
                        });
                }
            });

        // Add languages
        this._translateService.addLangs(["pt-BR"]);

        // Set the default language
        this._translateService.setDefaultLang("pt-BR");

        // Use a language
        this._translateService.use("pt-BR");

        // Add is-mobile class to the body if the platform is mobile
        if (this._platform.ANDROID || this._platform.IOS) {
            this.document.body.classList.add("is-mobile");
        }

        // Set the private defaults
        this._unsubscribeAll = new Subject();

        const requiredImplementation = {
            get: function (): boolean {
                if (this._required) {
                    return this._required;
                }

                // The required attribute is set
                // when the control return an error from validation with an empty value
                if (this.ngControl && this.ngControl.control && this.ngControl.control.validator) {
                    const emptyValueControl = Object.assign({}, this.ngControl.control);
                    (emptyValueControl as any).value = null;
                    return "required" in (this.ngControl.control.validator(emptyValueControl) || {});
                }
                return false;
            },
            set: function (value: boolean) {
                this._required = coerceBooleanProperty(value);
            },
        };
        Object.defineProperty(MatInput.prototype, "required", requiredImplementation);
        Object.defineProperty(MatSelect.prototype, "required", requiredImplementation);

        this._htmlInjectorService.init();

        this._prescriptionConfigService.prescriptionCommentsOrder$.subscribe();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        if (isSncrIntegrationEnabled() && this._sncrBrokerCallback.hasPendingBrokerSession()) {
            this._router.navigateByUrl(SNCR_INTEGRATION_ROUTE);
        }

        this._recordingState.navigateToConsultationRequested$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(async ({ patientId, returnUrl }) => {
                // Inside an attendance the in-attendance hub handles "expand"; don't navigate to the patient profile.
                if (this._recordingState.inAttendance()) {
                    return;
                }
                if (returnUrl) {
                    this._router.navigateByUrl(returnUrl);
                    return;
                }
                try {
                    const [{ PatientProfileComponent }, { MatPaginatorIntl }, { PortugueseMatPaginatorIntl }] =
                        await Promise.all([
                            import("@vhealth/patient"),
                            import("@angular/material/paginator"),
                            import("@app/wapps/core/pt-br-paginator-intl"),
                        ]);
                    this._location.replaceState("patients/" + patientId);
                    this.dialog.open(PatientProfileComponent, {
                        panelClass: "patient-profile-dialog",
                        data: { patientId, initialTab: "transcriptions" },
                        // The transcription list paginates; this dialog resolves from the root injector, which has
                        // no Portuguese MatPaginatorIntl since that would put the paginator in the initial bundle. (VS-1961)
                        injector: Injector.create({
                            providers: [{ provide: MatPaginatorIntl, useClass: PortugueseMatPaginatorIntl }],
                            parent: this._injector,
                        }),
                    });
                } catch (error) {
                    console.error("Failed to load the patient profile", error);
                    this._notificationService.error(
                        this._transloco.translate("patient-transcriptions.open-profile-error")
                    );
                }
            });

        SignalRAspNetCoreHelper.initSignalR();
        this._router.events.pipe(takeUntil(this._unsubscribeAll)).subscribe((item) => {
            if (item instanceof NavigationEnd) {
                const gtmTag = {
                    event: "page",
                    pageName: item.url,
                };
                this._gtmService.pushTag(gtmTag).then();
            }
        });

        // Subscribe to config changes
        this._fuseConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe((config) => {
            this.fuseConfig = config;

            if (this.fuseConfig.layout.width === "boxed") {
                this.document.body.classList.add("boxed");
            } else {
                this.document.body.classList.remove("boxed");
            }
        });
        window.addEventListener("message", (event) => {
            if (event.data.message === "User not authenticated in v2") {
                this._router.navigate(["account/login"]);
            }
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key): void {
        this._fuseSidebarService.getSidebar(key).toggleOpen();
    }
}
